import { useAnimatedSelection } from "@/hooks/use-animated-selection";
import { useIsMobile } from "@/hooks/use-mobile";
import { AgendaSection } from "../tasks/AgendaSection";
import { useCalendarTasks } from "../tasks/useCalendarTasks";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useGetIdentity, useGetMany, type Identifier } from "ra-core";
import { format, startOfDay, endOfDay } from "date-fns";
import { ru } from "date-fns/locale";
import { ArrowUpRight, CalendarDays, ListTodo, Wrench } from "lucide-react";

import { isInstallation } from "../tasks/installation";
import { personalTask, tasksOnDay } from "../tasks/calendarModel";
import { CalendarEvent } from "../tasks/CalendarEvent";
import { TaskWorkDialog } from "../tasks/TaskWorkDialog";

export function DashboardToday() {
  const { identity } = useGetIdentity();
  const mobile = useIsMobile();
  const [installationsOpen, setInstallationsOpen] = useState(false);
  const [tasksOpen, setTasksOpen] = useState(true);
  const admin = identity?.administrator === true;
  const today = new Date(),
    date = format(today, "yyyy-MM-dd");
  const [selected, setSelected, taskOpen] = useAnimatedSelection<Identifier>();
  const periodFilter = {
    "scheduled_start@lte": endOfDay(today).toISOString(),
    "scheduled_end@gt": startOfDay(today).toISOString(),
  };
  const personal = useCalendarTasks({
    ...periodFilter,
    ...(admin ? {} : { sales_id: identity?.id }),
  });
  const shared = useCalendarTasks({ ...periodFilter, team_task: true });
  const montage = useCalendarTasks({
    type: "installation",
    "installation_start@lte": date,
    "installation_end@gte": date,
  });
  const tasks = tasksOnDay(
    [
      ...new Map(
        [...(personal.data ?? []), ...(shared.data ?? [])]
          .filter((t) => !isInstallation(t))
          .map((t) => [String(t.id), t]),
      ).values(),
    ],
    today,
  );
  const installations = montage.data ?? [];
  const ids = [
    ...new Set(
      [...tasks, ...installations].flatMap((t) =>
        t.deal_id == null ? [] : [t.deal_id],
      ),
    ),
  ];
  const { data: projects = [] } = useGetMany<{
    id: Identifier;
    name: string;
    calendar_color?: string;
  }>("project_lookup", { ids }, { enabled: ids.length > 0 });
  const colors = new Map(projects.map((p) => [String(p.id), p.calendar_color]));
  const names = new Map(projects.map((p) => [String(p.id), p.name]));
  const error = personal.error || shared.error || montage.error;
  const busy = personal.isPending || shared.isPending || montage.isPending;
  const link = `/tasks?date=${date}&view=day`;
  return (
    <section
      data-mobile-reveal
      aria-label="События сегодня"
      className="rounded-2xl border bg-card p-4 md:p-5"
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <CalendarDays data-mobile-decoration className="size-4" />
            Сегодня
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {format(today, "d MMMM, EEEE", { locale: ru })}
          </p>
        </div>
        <Link
          to={link}
          className="rounded-full p-2 hover:bg-muted"
          data-mobile-control="compact"
          aria-label="Открыть сегодняшний день в календаре"
        >
          <ArrowUpRight className="size-4" />
        </Link>
      </div>
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          Не удалось загрузить события на сегодня
        </p>
      ) : busy ? (
        <p className="text-sm text-muted-foreground">Загрузка…</p>
      ) : (
        <>
          {installations.length > 0 && (
            <div className="mb-3">
              <TodayGroup
                title="Монтажи"
                count={installations.length}
                mobile={mobile}
                open={installationsOpen}
                onOpenChange={setInstallationsOpen}
                marker={<Wrench className="size-3.5" />}
              >
                {installations.slice(0, mobile ? 1 : 2).map((task) => (
                  <CalendarEvent
                    compact
                    quickStatus
                    installation
                    day={today}
                    key={task.id}
                    task={task}
                    color={colors.get(String(task.deal_id))}
                    onOpen={setSelected}
                  />
                ))}
                {installations.length > (mobile ? 1 : 2) && (
                  <Link
                    to={link}
                    className="text-xs text-primary hover:underline"
                  >
                    Все монтажи на сегодня ({installations.length})
                  </Link>
                )}
              </TodayGroup>
            </div>
          )}
          <TodayGroup
            title="Задачи"
            count={tasks.length}
            mobile={mobile}
            open={tasksOpen}
            onOpenChange={setTasksOpen}
            marker={<ListTodo className="size-3.5" />}
          >
            {tasks.slice(0, mobile ? 2 : 4).map((task) => (
              <CalendarEvent
                compact
                quickStatus
                key={task.id}
                task={task}
                day={today}
                color={colors.get(String(task.deal_id))}
                projectName={names.get(String(task.deal_id))}
                personal={admin && personalTask(task, identity?.id)}
                onOpen={setSelected}
              />
            ))}
            {tasks.length === 0 && (
              <p className="py-3 text-sm text-muted-foreground">
                На сегодня задач нет
              </p>
            )}
            {tasks.length > (mobile ? 2 : 4) && (
              <Link to={link} className="text-xs text-primary hover:underline">
                Все задачи на сегодня ({tasks.length})
              </Link>
            )}
          </TodayGroup>
        </>
      )}
      {selected != null && (
        <TaskWorkDialog
          open={taskOpen}
          taskId={selected}
          onOpenChange={(open) => {
            if (!open) setSelected(null);
          }}
        />
      )}
    </section>
  );
}

function TodayGroup({
  title,
  count,
  mobile,
  open,
  onOpenChange,
  marker,
  children,
}: {
  title: string;
  count: number;
  mobile: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  marker: ReactNode;
  children: ReactNode;
}) {
  if (mobile)
    return (
      <AgendaSection
        title={title}
        regionLabel={`${title} сегодня`}
        count={count}
        marker={marker}
        open={open}
        onOpenChange={onOpenChange}
      >
        {children}
      </AgendaSection>
    );
  return (
    <>
      <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
        {title} · {count}
      </h3>
      {children}
    </>
  );
}
